// Phase 5.4 · 간편 홈 대시보드 — 가이드형 워크플로우
// D-6E FINAL CLEANUP (2026-09-10) — §13/§14: OMS 8단계 canonical source 통일.
//   · STAGES / stageCount / fetchOmsCounts 는 `@/lib/omsStages` 를 REUSE
//     · SimpleOrders.js 와 동일 정의를 공유하여 홈/주문관리/실제 목록 total
//       세 곳이 반드시 동일한 canonical count 를 사용하도록 한다.
//   · §11: "오늘 할 일" 은 각 stage 에 현재 머무는 ACTIVE 주문 수. 누적/과거
//       처리 총계 아님.  주문 데이터 자체는 이전과 동일하게 보존 (§10/§17/§22).
//   · 백엔드 API 변경 없음 · 재사용: /seller/oms/summary · /seller/orders ·
//       /seller/dashboard/ops-summary.
import React, { useEffect, useState } from 'react';
import { Loader2, AlertTriangle, ArrowRight, Radio, Plus, Boxes, ShoppingBag, Users, ExternalLink, Link2 } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { STAGES, stageCount, fetchOmsCounts } from '@/lib/omsStages';

const won = (n) => `${(Number(n) || 0).toLocaleString()}원`;

export default function SimpleHome({ goTo, seller }) {
  const [ops, setOps] = useState(null);
  const [counts, setCounts] = useState({ oms: {}, pay_pending: 0, shipping: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [canon, rOps] = await Promise.all([
          fetchOmsCounts(api),
          api.get('/seller/dashboard/ops-summary').catch(() => ({ data: {} })),
        ]);
        if (!alive) return;
        setCounts(canon);
        setOps(rOps.data || {});
      } catch {
        toast.error('요약 정보를 불러오지 못했습니다.');
      } finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, []);

  if (loading) {
    return <div className="flex items-center gap-2 text-sm text-gray-500 py-16 justify-center" data-testid="simple-home-loading"><Loader2 className="animate-spin" size={16} /> 불러오는 중…</div>;
  }

  const todos = ops?.todos || {};
  const problemCount = (todos.stock_shortage?.count || 0)
                      + (todos.rma_pending?.count || 0)
                      + (todos.inbound_waiting?.count || 0);
  const summary = ops?.today_summary || {};

  return (
    <div className="space-y-6" data-testid="simple-home-section">
      <div>
        <p className="text-xs font-bold uppercase tracking-widest text-gray-400">오늘의 할 일</p>
        <h2 className="mt-1 text-2xl md:text-3xl font-black tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }} data-testid="simple-home-title">
          {seller?.name || '판매자'}님, 오늘 처리할 주문을 확인해보세요.
        </h2>
        <p className="mt-1 text-xs text-gray-500">각 단계에 현재 머무는 주문 건수입니다. 다음 단계로 이동하면 이전 단계 카운트에서 빠집니다.</p>
      </div>

      {seller?.seller_id && (
        <div className="rounded-2xl border border-black/10 dark:border-white/10 bg-gradient-to-r from-[#FF3B30]/5 to-transparent p-4 flex items-center gap-3" data-testid="dashboard-profile-cta">
          <div className="flex-1 min-w-0">
            <p className="text-sm font-black">🎯 이 링크 하나로 내 방송·쇼츠·상품·공동구매·쿠폰 전부 홍보하세요.</p>
            <p className="text-[11px] text-gray-500 truncate mt-0.5">{`${window.location.origin}/seller/${seller.seller_id}`}</p>
          </div>
          <a href={`/seller/${seller.seller_id}`} target="_blank" rel="noreferrer" className="shrink-0 inline-flex items-center gap-1 px-3 py-2 rounded-full bg-[#FF3B30] hover:bg-[#D32F2F] text-white text-xs font-black" data-testid="dashboard-view-profile">
            <ExternalLink size={11}/> 내 프로필 보기
          </a>
          <button onClick={async () => {
              const url = `${window.location.origin}/seller/${seller.seller_id}`;
              try { await navigator.clipboard.writeText(url); toast.success('프로필 링크가 복사되었습니다'); }
              catch { toast.error('복사에 실패했습니다'); }
            }} className="shrink-0 inline-flex items-center gap-1 px-3 py-2 rounded-full border border-black/10 hover:border-[#FF3B30] text-xs font-bold" data-testid="dashboard-copy-profile-link">
            <Link2 size={11}/> 링크 복사
          </button>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="simple-home-summary-cards">
        <MiniStat label="오늘 주문" value={summary.orders || 0} suffix="건" testId="mini-stat-orders" />
        <MiniStat label="오늘 판매 수량" value={summary.qty || 0} suffix="개" testId="mini-stat-qty" />
        <MiniStat label="결제완료 금액" value={won(summary.paid_amount || 0)} testId="mini-stat-paid" />
        <MiniStat label="오늘 출고 완료" value={summary.shipped || 0} suffix="건" testId="mini-stat-shipped" />
      </div>

      {/* D-6E FINAL CLEANUP §14 · 8-stage 트래커 (⑤ 출고관리 복구 · 이전 7단계 UI 폐기).
          §13 · STAGES / stageCount 는 omsStages 로 통일. */}
      <div>
        <div className="flex items-baseline justify-between mb-3">
          <h3 className="text-sm font-black tracking-widest uppercase">단계별 처리</h3>
          <button onClick={() => goTo('orders')} className="text-xs font-bold text-[#FF3B30] hover:underline" data-testid="simple-home-goto-orders">
            전체 주문 관리 →
          </button>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-2" data-testid="simple-home-stage-tracker">
          {STAGES.map((s, i) => (
            <StageCard
              key={s.key}
              stage={s}
              count={stageCount(counts, s)}
              isLast={i === STAGES.length - 1}
              onGo={() => goTo('orders', { stage: s.key })}
            />
          ))}
        </div>
      </div>

      {problemCount > 0 && (
        <div className="rounded-xl border-2 border-amber-400/60 bg-amber-50/70 dark:bg-amber-500/10 p-4" data-testid="simple-home-problem-orders">
          <div className="flex items-start gap-3">
            <AlertTriangle className="text-amber-600 flex-shrink-0 mt-0.5" size={20} />
            <div className="flex-1 min-w-0">
              <p className="font-black text-sm text-amber-900 dark:text-amber-200">⚠ 문제 주문 · 확인 필요</p>
              <p className="text-xs text-amber-800/80 dark:text-amber-200/70 mt-0.5">아래 항목은 판매자 처리가 필요한 예외 상황입니다.</p>
              <div className="mt-3 flex flex-wrap gap-2" data-testid="simple-home-problem-chips">
                {todos.stock_shortage?.count > 0 && (
                  <ProblemChip label="재고 부족" count={todos.stock_shortage.count} onClick={() => goTo('inventory', { alert: 'shortage' })} testId="problem-chip-shortage" />
                )}
                {todos.rma_pending?.count > 0 && (
                  <ProblemChip label="취소·교환·반품" count={todos.rma_pending.count} onClick={() => goTo('rma')} testId="problem-chip-rma" />
                )}
                {todos.inbound_waiting?.count > 0 && (
                  <ProblemChip label="입고 대기" count={todos.inbound_waiting.count} onClick={() => goTo('inventory', { detail: 'inbound' })} testId="problem-chip-inbound" />
                )}
                {todos.po_pending?.count > 0 && (
                  <ProblemChip label="발주 승인 대기" count={todos.po_pending.count} onClick={() => goTo('inventory', { detail: 'purchase' })} testId="problem-chip-po" />
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="simple-home-quick-actions">
        <QuickButton icon={Radio} label="라이브 관리" onClick={() => goTo('live')} testId="qa-live" />
        <QuickButton icon={Plus} label="새 상품 등록" onClick={() => goTo('products', { action: 'new' })} testId="qa-new-product" />
        <QuickButton icon={Boxes} label="재고 확인" onClick={() => goTo('inventory')} testId="qa-inventory" />
        <QuickButton icon={Users} label="고객 · CS" onClick={() => goTo('customers')} testId="qa-customers" />
      </div>
    </div>
  );
}

const MiniStat = ({ label, value, suffix, testId }) => (
  <div className="rounded-lg border border-black/5 dark:border-white/10 bg-white dark:bg-[#141414] p-3">
    <p className="text-[10px] font-bold uppercase tracking-widest text-gray-500">{label}</p>
    <p className="mt-1 text-lg font-black tabular-nums" style={{ fontFamily: 'Manrope, sans-serif' }} data-testid={testId}>
      {typeof value === 'number' ? value.toLocaleString() : value}
      {suffix && <span className="text-xs text-gray-400 ml-0.5">{suffix}</span>}
    </p>
  </div>
);

const StageCard = ({ stage, count, isLast, onGo }) => {
  const has = count > 0;
  return (
    <button
      onClick={onGo}
      className={`relative w-full text-left p-3 rounded-lg border-2 transition ${
        has
          ? 'border-[#FF3B30]/70 bg-[#FF3B30]/5 hover:-translate-y-0.5 hover:border-[#FF3B30]'
          : 'border-black/10 dark:border-white/10 bg-white dark:bg-[#141414] hover:border-black/20'
      }`}
      data-testid={`stage-card-${stage.key}`}
    >
      <p className={`text-[11px] font-bold ${has ? 'text-[#FF3B30]' : 'text-gray-500'}`}>{stage.label}</p>
      <p className="mt-1 text-2xl font-black tabular-nums" data-testid={`stage-count-${stage.key}`}>
        {count.toLocaleString()}<span className="text-xs font-bold text-gray-400 ml-0.5">건</span>
      </p>
      <p className="mt-1 text-[10px] text-gray-500 line-clamp-2">{stage.desc || ''}</p>
      {!isLast && (
        <ArrowRight size={12} className="hidden lg:block absolute -right-2.5 top-1/2 -translate-y-1/2 text-gray-300 dark:text-white/10 bg-white dark:bg-[#0A0A0A] rounded-full" />
      )}
    </button>
  );
};

const ProblemChip = ({ label, count, onClick, testId }) => (
  <button onClick={onClick} data-testid={testId}
    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white dark:bg-[#141414] border border-amber-500/40 text-xs font-bold hover:border-amber-600 hover:-translate-y-0.5 transition">
    <span>{label}</span>
    <span className="px-1.5 rounded-full bg-amber-500 text-white text-[10px] tabular-nums">{count}</span>
  </button>
);

const QuickButton = ({ icon: Icon, label, onClick, testId }) => (
  <button onClick={onClick} data-testid={testId}
    className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-black/10 dark:border-white/10 bg-white dark:bg-[#141414] text-sm font-bold hover:border-[#FF3B30] hover:text-[#FF3B30] transition">
    <Icon size={15} />
    <span>{label}</span>
  </button>
);
